/**
 * What a distributing import is about to do, before it does it.
 *
 * Written after an 823,162-row file spent 3.3 hours being refused by an
 * endpoint that had refused the first row, having already been imported twice
 * by accident. Everything here can be read in a few seconds, and all of it is
 * advice: the import can still be started.
 */
import { useState } from 'react';
import { Alert, Badge, Button, Checkbox, Group, Paper, Stack, Table, Text } from '@mantine/core';
import { useImportPreflight, useImportTestSend, type TestSendResponse } from '../api/queries';
import { formatDateTime, formatNumber } from '../theme';
import { useVocab } from '../vocab';
import { formatDuration, importStatusLabel } from './ImportJobCard';

/** The number "Send the first rows only" sends. */
export const SAMPLE_ROWS = 100;

export function ImportPreflight({
  campaignId,
  sheetId,
  fileName,
  totalRows,
  columnMapping,
  sampleFirst,
  onSampleFirstChange,
}: {
  campaignId: string;
  sheetId: string | null;
  fileName: string;
  totalRows: number;
  columnMapping: Record<string, string>;
  sampleFirst: boolean;
  onSampleFirstChange: (next: boolean) => void;
}) {
  const v = useVocab();
  const preflight = useImportPreflight({
    campaign_id: campaignId,
    sheet_id: sheetId,
    file_name: fileName,
    total_rows: totalRows,
    column_mapping: columnMapping,
  });
  const testSend = useImportTestSend();
  const [tested, setTested] = useState<TestSendResponse | null>(null);
  const data = preflight.data;
  if (!data) return null;

  const does = data.does;
  const chips = [
    does.sells && (does.buyers === 1 ? `Sells to 1 ${v.one('buyer')}` : `Sells to ${does.buyers} ${v.many('buyer')}`),
    does.assigns && 'Assigns to reps',
    does.sends && 'Sends to a webhook',
    does.stores_only && 'Stores only',
  ].filter(Boolean) as string[];
  const failures = tested?.results.filter((r) => !r.accepted) ?? [];
  const noContact = !data.maps_email && !data.maps_phone;

  return (
    <Paper withBorder p="sm" radius="md" data-testid="import-preflight">
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <div>
            <Text size="sm" fw={600}>Before you send</Text>
            {data.estimated_seconds != null && (
              <Text size="sm">
                About {formatDuration(sampleFirst ? Math.min(SAMPLE_ROWS, totalRows) / data.rate_per_sec : data.estimated_seconds)}, at
                about {formatNumber(Math.round(data.rate_per_sec))} rows a second
                <Text span size="xs" c="dimmed">
                  {' '}
                  ({data.rate_source === 'recent' ? 'from recent imports' : 'an estimate, a slower endpoint takes longer'})
                </Text>
              </Text>
            )}
          </div>
          <Group gap={4} wrap="wrap" justify="flex-end">
            {chips.map((c) => <Badge key={c} size="sm" variant="light" color="gray">{c}</Badge>)}
          </Group>
        </Group>

        {data.routes.length > 0 && (
          <div>
            <Group justify="space-between" mb={4}>
              <Text size="xs" c="dimmed">
                {data.routes.length === 1 ? `1 live ${v.one('route')}` : `${data.routes.length} live ${v.many('route')}`}
              </Text>
              <Button
                size="compact-xs"
                variant="light"
                loading={testSend.isPending}
                onClick={() => testSend.mutate(campaignId, { onSuccess: setTested })}
              >
                Send a test
              </Button>
            </Group>
            <Table withRowBorders={false} verticalSpacing={2}>
              <Table.Tbody>
                {data.routes.map((r) => {
                  const result = tested?.results.find((t) => t.route_id === r.route_id);
                  return (
                    <Table.Tr key={r.route_id}>
                      <Table.Td>
                        <Text size="xs">{r.route_name}</Text>
                        {r.buyer_name && <Text size="xs" c="dimmed">{r.buyer_name}</Text>}
                      </Table.Td>
                      <Table.Td>
                        {result ? (
                          <Text size="xs" c={result.accepted ? 'chartGreen' : 'chartRed'} fw={600}>
                            {result.accepted ? 'Accepted' : 'Not accepted'}
                            {result.status != null ? ` · replied ${result.status}` : ''}
                            {result.reason ? `: ${result.reason}` : result.reply ? `: ${result.reply}` : ''}
                            {result.error ? `: ${result.error}` : ''}
                          </Text>
                        ) : (
                          <Text size="xs" c="dimmed">{r.concurrency} at once</Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
            <Text size="xs" c="dimmed" mt={4}>
              {`A test posts one made-up row to each ${v.one('route')}, for real. No row is created and nothing is recorded.`}
            </Text>
          </div>
        )}

        {failures.length > 0 && (
          <Alert color="chartRed" variant="filled" title={`${failures.length === 1 ? `A ${v.one('route')} is` : `${failures.length} ${v.many('route')} are`} not accepting rows`}>
            <Text size="sm">
              If a {v.one('route')} keeps refusing, the import stops itself after 100 rejections in a row, but it is
              better fixed now. You can still start the import.
            </Text>
          </Alert>
        )}

        {does.stores_only && (
          <Alert color="chartAmber" variant="light">
            <Text size="sm">{`No ${v.one('route')} is live on this ${v.one('campaign')}, so these rows would only be stored.`}</Text>
          </Alert>
        )}

        {noContact && (
          <Alert color="chartAmber" variant="light" title="No email or phone mapped">
            <Text size="sm">
              {`Duplicate checks by email or phone will not apply, and ${v.many('buyer')} receive only the mapped fields.`}
            </Text>
          </Alert>
        )}

        {data.previous.length > 0 && (
          <Alert color="chartAmber" variant="light" title="This file looks already imported here">
            <Stack gap={2}>
              {data.previous.map((p) => (
                <Text size="sm" key={p.job_id}>
                  {formatDateTime(p.created_at)}: {formatNumber(p.rows_imported ?? 0)} rows ({importStatusLabel(p.status, false).toLowerCase()})
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        <Checkbox
          label={`Send the first ${SAMPLE_ROWS} rows only`}
          description="See how they do before committing the whole file. The rest are not imported until you say so."
          checked={sampleFirst}
          onChange={(e) => onSampleFirstChange(e.currentTarget.checked)}
        />
      </Stack>
    </Paper>
  );
}
